import React, { useCallback, useEffect, useState } from 'react';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Search from '../../shared/components/ui/Search';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Table from '../../shared/components/layout/Table';
import { transformData } from '../../super-admin/utils/transformData';
import DateFormat from '../../super-admin/utils/DateFormat';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  buildReturnToFromLocation,
  navigateWithReturn
} from '../../shared/navigation';
import toast from 'react-hot-toast';
import { filterData } from '../../shared/utils/filterData';
import { UpdateStatus } from '../components/forms/UpdateStatus';
import Modal from '../../shared/components/ui/Modal';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getOrders,
  getRequirements,
  updateOrdersStatus,
  downloadInvoiceForOrder,
  downloadBillForOrder,
  getAllRFQs,
  getMyVendors,
  updateRFQ
} from '../services/api/implementation/impl';
import NoDataFound from '../components/layout/NoDataFound';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Loader from '../../shared/components/ui/Loader';
import Multiselect from 'multiselect-react-dropdown';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const Orders = () => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed } = useSidebar();
  // pagination
  const [requirementPage, setRequirementPage] = useState(1);
  const [ordersPage, setOrdersPage] = useState(1);
  const [requirementLimit] = useState(10); // items per page
  const [ordersLimit] = useState(10); // items per page
  const [projectID, setProjectID] = useState(null);
  // Separate filters for requirements and orders
  const [requirementFilters, setRequirementFilters] = useState(() => {
    const saved = localStorage.getItem('ordersRequirementFilters');
    return saved ? JSON.parse(saved) : { project: null };
  });
  const [ordersFilters, setOrdersFilters] = useState(() => {
    const saved = localStorage.getItem('ordersOrderFilters');
    return saved ? JSON.parse(saved) : { project: null };
  });

  // Query to get requirements
  const {
    data: getAllrequiremnt,
    refetch: refetchRequiremnt,
    isFetching: isFetchingRequiremnt
  } = useQuery({
    queryKey: ['requirement', requirementPage, requirementFilters.projectId],
    queryFn: () => getAllRFQs(requirementPage, requirementLimit, requirementFilters.projectId),
    keepPreviousData: true
  });

  // Query to get orders
  const {
    data: getAllOrders,
    refetch: refetchgetAllOrders,
    isFetching: isFetchingOrders
  } = useQuery({
    queryKey: ['orders', ordersPage, ordersFilters.projectId],
    queryFn: () => getOrders(ordersPage, ordersLimit, ordersFilters.projectId),
    keepPreviousData: true
  });

  const { data: allVendors } = useQuery({
    queryKey: ['allVendors'],
    queryFn: () => getMyVendors(1, 500)
  });

  useEffect(() => {
    refetchRequiremnt();
    refetchgetAllOrders();
  }, []);

  // Project IDs for each tab
  const requirementProjectID = requirementFilters.projectId || null;
  const ordersProjectID = ordersFilters.projectId || null;

  // Save filters to localStorage
  const saveRequirementFilters = (filters) => {
    localStorage.setItem('ordersRequirementFilters', JSON.stringify(filters));
  };
  const saveOrdersFilters = (filters) => {
    localStorage.setItem('ordersOrderFilters', JSON.stringify(filters));
  };

  // Clear filter for each tab
  const clearRequirementFilter = () => {
    setRequirementFilters({ project: null, projectId: null });
    saveRequirementFilters({ project: null, projectId: null });
  };
  const clearOrdersFilter = () => {
    setOrdersFilters({ project: null, projectId: null });
    saveOrdersFilters({ project: null, projectId: null });
  };

  // Mutations to handle invoice download
  const { mutate: handleDownloadInvoice } = useMutation({
    mutationFn: async (id) => {
      return await downloadInvoiceForOrder(id);
    },
    onSuccess: (data, id) => {
      if (!data || data.byteLength === 0) {
        toast.error('Error: File data is empty or invalid.');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Order_${id}_Invoice.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Invoice Downloaded');
    },
    onError: (error) => {
      console.error('Error downloading invoice:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error in downloading invoice'
      );
    }
  });

  // Mutations to handle bill download
  const { mutate: handleDownloadBill } = useMutation({
    mutationFn: async (id) => {
      return await downloadBillForOrder(id);
    },
    onSuccess: (data, id) => {
      if (!data || data.byteLength === 0) {
        toast.error('Error: File data is empty or invalid.');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `Order_${id}_Bill.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Bill Downloaded');
    },
    onError: (error) => {
      console.error('Error downloading bill:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Error in downloading bill');
    }
  });

  // total pagination pages
  const totalRequirementPages = getAllrequiremnt?.meta
    ? Math.ceil(getAllrequiremnt?.meta.total / requirementLimit)
    : 1;

  const totalOrdersPage = getAllOrders?.meta
    ? Math.ceil(getAllOrders?.meta.total / ordersLimit)
    : 1;

  // Local state
  const [requirements, setrequirements] = useState([]);
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Retrieve the tab from localStorage, fallback to 'requirements' if nothing is stored
  const [activeTab, setActiveTab] = useState(() => {
    const storedTab = localStorage.getItem('ordersActiveTab');
    return storedTab || 'requirements';
  });

  const [filteredData, setFilteredData] = useState([]);
  const [openUpdateStatus, setOpenUpdateStatus] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEditRFQ, setShowEditRFQ] = useState(false);
  const [transformedVendors, setTransformedVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [editingRFQId, setEditingRFQId] = useState(null);
  const [rfqData, setRfqData] = useState({
    project_id: null,
    vendor_ids: null,
    paymentTerms: null,
    termsAndCondition: null,
    deliveryDate: null,
    items: null
  });
  // Track page view
  useEffect(() => {
    mixpanel.track('Orders Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchgetAllOrders();
    refetchRequiremnt();
  }, [activeTab]);

  // Refetch requirements when requirementFilters.projectId changes
  useEffect(() => {
    refetchRequiremnt();
  }, [requirementFilters.projectId]);

  // Refetch orders when ordersFilters.projectId changes
  useEffect(() => {
    refetchgetAllOrders();
  }, [ordersFilters.projectId]);

  // set data from API to local states
  useEffect(() => {
    if (getAllrequiremnt?.data) {
      setrequirements(getAllrequiremnt?.data);
    }
  }, [getAllrequiremnt?.data]);

  useEffect(() => {
    if (getAllOrders?.data) {
      setOrders(getAllOrders.data);
    }
  }, [getAllOrders?.data]);

  // handle tab changes and persist in localStorage
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('ordersActiveTab', tab);
  };

  // handle searching
  const handleSearchChange = useCallback(
    (query) => {
      setSearchQuery(query);

      let currentData = [];
      if (activeTab === 'requirements') {
        currentData = [...requirements];
      } else if (activeTab === 'orders') {
        currentData = [...orders];
      }

      const filteredData = filterData('vendor', currentData, query);
      setFilteredData(filteredData);
    },
    [activeTab, requirements, orders]
  );

  // handle searching on button click
  const handleSearch = useCallback(() => {
    let currentData = [];
    if (activeTab === 'requirements') {
      currentData = [...requirements];
    } else if (activeTab === 'orders') {
      currentData = [...orders];
    }
    const filteredData = filterData('vendor', currentData, searchQuery);
    setFilteredData(filteredData);
  }, [activeTab, requirements, orders, searchQuery]);

  // handle delete
  const handleDelete = (id) => {
    let currentData;
    if (activeTab === 'requirements') {
      currentData = [...requirements];
      setrequirements(currentData.filter((item) => item.id !== id));
    } else {
      currentData = [...orders];
      setOrders(currentData.filter((item) => item.id !== id));
    }
    const updatedData = currentData.filter((item) => item.id !== id);
    setFilteredData(updatedData);
    toast.success('Deleted successfully');
  };

  // effect to set filtered data whenever activeTab changes or data changes
  useEffect(() => {
    let currentData = [];
    if (activeTab === 'requirements') {
      currentData = requirements;
    } else if (activeTab === 'orders') {
      currentData = orders;
    }
    setFilteredData(currentData);
  }, [activeTab, requirements, orders]);

  // handle formatting
  const handleRequirementFormant = (item) => {
    return {
      id: item?.rfqItems[0]?.rfq_id,
      company: item?.company?.company_name,
      project:
        [item?.project?.name, item?.block?.name, item?.unit?.unitName].filter(Boolean).join(' /') ||
        ' ',
      category: Array.from(new Set(item?.rfqItems?.map((item) => item?.category?.name))).join(', '),
      vendors: item?.vendors?.length || '0',
      noOfItems: item?.rfqItems?.length || '0',
      createdAt: item?.created_at ? DateFormat(item.created_at) : 'N/A',
      status: item?.status
    };
  };

  const handleOrders = (item) => {
    return {
      id: item?.id,
      po_id: item?.id,
      project: item?.project?.name,
      vendor: item?.vendor?.user?.name,
      po_value:
        item?.total_amount !== undefined
          ? `₹ ${item?.total_amount?.toLocaleString('en-IN')}`
          : '₹ 0',
      status: item?.status
      // actions: 'Actions'
    };
  };

  // transform data for requirements
  const requirementData =
    getAllrequiremnt?.data?.length > 0
      ? getAllrequiremnt?.data?.map((item) =>
          transformData(handleRequirementFormant(item), [
            'id',
            'company',
            'project',
            'category',
            'vendors',
            'noOfItems',
            'createdAt',
            'status',
            'actions'
          ])
        )
      : [];

  // transform data for orders
  const OrdersData = getAllOrders?.data?.length
    ? getAllOrders?.data?.map(
        (item) => handleOrders(item),
        ['id', 'po_id', 'project', 'vendor', 'po_value', 'status']
      )
    : [];

  // Status Update for the Orders
  const UpdateStatusOptions = [
    {
      id: 1,
      name: 'Material received'
    },
    {
      id: 2,
      name: 'Payment Done'
    }
  ];

  const { mutate: handleStatusUpdateFn } = useMutation({
    mutationFn: (payload) => updateOrdersStatus(parseInt(orderId), payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Status Updated Successfully');
      setOpenUpdateStatus(false);
      refetchgetAllOrders();
    },
    onError: (err) => {
      setIsSubmitting(false);
      toast.error(err.response.data.message || err?.message || 'Error in Status Update');
    }
  });

  // Open modal for status update
  const handleStatusUpdate = (id) => {
    setOrderId(id);
    setOpenUpdateStatus(true);
  };

  // handle pagination
  const handleRequirementPageChange = (newPage) => {
    setrequirements([]);
    setFilteredData([]);
    setSearchQuery('');
    setRequirementPage(newPage);
  };

  const handleOrdersPageChange = (newPage) => {
    setOrders([]);
    setFilteredData([]);
    setSearchQuery('');
    setOrdersPage(newPage);
  };

  // handle viewing details of requirements
  const handleViewDetails = (id) => {
    navigateWithReturn(navigate, `/builder/inventory/orders/requirement/${id}`, {
      returnTo: buildReturnToFromLocation(location)
    });
  };

  const handlestatusUpdate = (data) => {
    const payload = { ...data }; // shallow copy to avoid mutation issues
    console.log('payload', payload);
    handleStatusUpdateFn(payload);
  };

  // Handle vendor selection for edit RFQ
  const handleVendorSelect = (vendorIds) => {
    console.log('vendorIds', vendorIds);
    // Ensure we only store valid vendor IDs that exist in transformedVendors
    const validVendorIds = vendorIds.filter((id) =>
      transformedVendors.some((vendor) => vendor.id === id)
    );
    setSelectedVendors(validVendorIds);
  };

  console.log('selectedVendors', selectedVendors);

  // Handle edit RFQ submit
  const handleEditRFQSubmit = async () => {
    if (selectedVendors.length === 0) {
      toast.error('Please select at least one vendor');
      return;
    }

    const payload = {
      ...rfqData,
      vendor_ids: selectedVendors
    };

    console.log('Edit RFQ Payload:', payload);
    console.log('RFQ ID:', editingRFQId);

    try {
      await updateRFQ(editingRFQId, payload);
      toast.success('RFQ updated successfully');
      setShowEditRFQ(false);
      setSelectedVendors([]);
      setEditingRFQId(null);
      refetchRequiremnt();
    } catch (error) {
      console.error('Error updating RFQ:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Error updating RFQ');
    }
  };

  const handleEditRFQ = (id) => {
    const rfq = getAllrequiremnt?.data?.find((item) => item.id === Number(id));
    if (rfq?.status === 'Expired') {
      toast.error('RFQ is expired');
      return;
    }
    if (rfq) {
      setEditingRFQId(id);
      setSelectedVendors([]);
      setRfqData({
        project_id: rfq?.project_id,
        paymentTerms: rfq?.paymentTerms,
        termsAndCondition: rfq?.termsAndCondition,
        deliveryDate: rfq?.deliveryDate,
        items: rfq?.rfqItems?.map((item) => ({
          id: item?.id
        }))
      });

      // Get existing vendor IDs from the RFQ
      const existingVendorIds = rfq?.vendors?.map((vendor) => vendor.vendor_id) || [];

      const filteredVendors =
        allVendors?.data?.filter((vendor) => {
          // Exclude vendors that are already in the RFQ
          if (existingVendorIds.includes(vendor.id)) {
            return false;
          }

          // Check if vendor has category mappings
          if (!vendor?.vendorCategoryMapping || vendor?.vendorCategoryMapping?.length === 0) {
            return false;
          }

          // Check if vendor has any category that matches the selected items' categories
          const vendorCategoryIds = vendor?.vendorCategoryMapping?.map(
            (mapping) => mapping.category_id
          );
          return rfq?.rfqItems?.some((item) => vendorCategoryIds.includes(item?.category_id));
        }) || [];

      const transformedVendors = filteredVendors?.map((vendor) => {
        // Get matching categories for this vendor
        const matchingCategories = vendor?.vendorCategoryMapping
          .filter((mapping) =>
            rfq?.rfqItems?.some((item) => item?.category_id === mapping.category_id)
          )
          .map((mapping) => mapping.categroy?.name)
          .filter(Boolean);

        const categoryText =
          matchingCategories.length > 0 ? ` (${matchingCategories.join(', ')})` : '';

        return {
          name: `${vendor?.user?.name || ''}${categoryText}`,
          id: vendor?.id
        };
      });
      setTransformedVendors(transformedVendors);
      setShowEditRFQ(true);
    }
  };

  return (
    <>
      <Loader isVisible={isFetchingRequiremnt || isFetchingOrders || isProjectsLoading} />
      {openUpdateStatus && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={40}
          name="Update Status"
          onClose={() => setOpenUpdateStatus(false)}>
          <UpdateStatus
            onSubmit={handlestatusUpdate}
            statuses={UpdateStatusOptions}
            isSubmitting={isSubmitting}
            onClose={() => setOpenUpdateStatus(false)}
          />
        </Modal>
      )}

      {showEditRFQ && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={50}
          name="Edit RFQ"
          onClose={() => setShowEditRFQ(false)}>
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Vendors</h3>

              <div className="mb-4">
                <Multiselect
                  options={transformedVendors}
                  selectedValues={selectedVendors
                    .map((id) => transformedVendors.find((vendor) => vendor.id === id))
                    .filter(Boolean)}
                  onSelect={(selectedList, selectedItem) => {
                    console.log('onSelect called:', selectedList, selectedItem);
                    const vendorIds = selectedList
                      .filter((item) => item && item.id) // Filter out undefined/null items
                      .map((item) => item.id);
                    handleVendorSelect(vendorIds);
                  }}
                  onRemove={(selectedList, removedItem) => {
                    console.log('onRemove called:', selectedList, removedItem);
                    const vendorIds = selectedList
                      .filter((item) => item && item.id) // Filter out undefined/null items
                      .map((item) => item.id);
                    handleVendorSelect(vendorIds);
                  }}
                  placeholder="Select vendors..."
                  displayValue="name"
                  trackBy="id"
                  showCheckbox={true}
                  avoidHighlightFirstOption={true}
                />
              </div>

              {transformedVendors.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-gray-500">No vendors found for the selected items.</p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={() => setShowEditRFQ(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                Cancel
              </button>
              <button
                onClick={handleEditRFQSubmit}
                disabled={selectedVendors.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed">
                Update RFQ
              </button>
            </div>
          </div>
        </Modal>
      )}
      <Layout>
        {/* Header */}
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header data={{ name: 'Orders' }} />
        </div>

        {/* Tabs & Search */}
        <div
          className={`flex z-20 items-center justify-start gap-10 px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100 shadow-sm transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <div className="flex gap-8">
            <p
              className={`text-gray-500 py-2 cursor-pointer ${
                activeTab === 'requirements' ? 'border-b-2 border-primary text-primary' : ''
              }`}
              onClick={() => handleTabChange('requirements')}>
              All RFQs
            </p>
            <p
              className={`text-gray-500 py-2 cursor-pointer ${
                activeTab === 'orders' ? 'border-b-2 border-primary text-primary' : ''
              }`}
              onClick={() => handleTabChange('orders')}>
              Purchase Order
            </p>
          </div>

          {/* Filter Indicator */}
          {activeTab === 'requirements' && requirementFilters.project && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600">Filtering by:</span>
              <div className="flex items-center gap-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                  {requirementFilters.project}
                  <button
                    onClick={clearRequirementFilter}
                    className="ml-1 text-blue-600 hover:text-blue-800 font-bold text-sm">
                    ×
                  </button>
                </span>
              </div>
            </div>
          )}
          {activeTab === 'orders' && ordersFilters.project && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600">Filtering by:</span>
              <div className="flex items-center gap-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                  {ordersFilters.project}
                  <button
                    onClick={clearOrdersFilter}
                    className="ml-1 text-blue-600 hover:text-blue-800 font-bold text-sm">
                    ×
                  </button>
                </span>
              </div>
            </div>
          )}
          {/* <Search onChange={handleSearchChange} query={searchQuery} onClick={handleSearch} /> */}
        </div>

        {/* Main content */}
        <GridWrapper width={100}>
          <div className="mt-[10rem] h-[calc(100vh-12rem)] overflow-auto relative table-container">
            <style>{`
            .table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .table-container .table-container {
              height: auto !important;
            }
            .table-container > div {
              height: auto !important;
            }
          `}</style>
            {activeTab === 'requirements' &&
              (requirementData && requirementData?.length > 0 ? (
                <>
                  <Table
                    headers={[
                      'Company',
                      {
                        label: 'Project',
                        dropdown: true,
                        dropdownOptions: [
                          { id: '', value: '', label: 'Select Project', selected: true },
                          ...projectsForDropdown
                        ],
                        onDropdownSelect: (id) => {
                          const selectedProject = projects?.find((p) => p.id === id);
                          const newFilters = {
                            project: selectedProject?.name || null,
                            projectId: id
                          };
                          setRequirementFilters(newFilters);
                          saveRequirementFilters(newFilters);

                          // Track filter change
                          mixpanel.track('Requirement List Filter Changed', {
                            user_id: userId,
                            filter_type: 'project',
                            project_id: id,
                            timestamp: new Date().toISOString()
                          });
                        }
                      },
                      'Category',
                      'Vendors',
                      'Items',
                      'Created At',
                      'Status',
                      'Actions'
                    ]}
                    data={requirementData}
                    identifier={'id'}
                    isEditEnable={true}
                    onEdit={(id) => handleEditRFQ(id)}
                    handleRowClick={handleViewDetails}
                    //isViewByName={true}
                    rowName={'View Quotations'}
                    isWrap={true}
                  />
                  <PaginationComponent
                    page={requirementPage}
                    totalPages={totalRequirementPages}
                    onPageChange={handleRequirementPageChange}
                    isFetching={isFetchingRequiremnt}
                  />
                </>
              ) : (
                <NoDataFound disabledBtn={true} screenHeight={false} />
              ))}

            {activeTab === 'orders' &&
              (OrdersData && OrdersData?.length > 0 ? (
                <>
                  <Table
                    headers={[
                      'PO ID',
                      {
                        label: 'Project',
                        dropdown: true,
                        dropdownOptions: [
                          { id: '', value: '', label: 'Select Project', selected: true },
                          ...projectsForDropdown
                        ],
                        onDropdownSelect: (id) => {
                          const selectedProject = projects?.find((p) => p.id === id);
                          const newFilters = {
                            project: selectedProject?.name || null,
                            projectId: id
                          };
                          setOrdersFilters(newFilters);
                          saveOrdersFilters(newFilters);
                          mixpanel.track('Orders List Filter Changed', {
                            user_id: userId,
                            filter_type: 'project',
                            project_id: id,
                            timestamp: new Date().toISOString()
                          });
                        }
                      },
                      'Vendor',
                      'PO Value',
                      'Status'
                    ]}
                    data={OrdersData}
                    identifier={'id'}
                    isEditEnable={false}
                    isStatusUpdateEnable={true}
                    onStatusUpdate={(id) => handleStatusUpdate(id)}
                    isDownloadInvoiceEnable={true}
                    onDownloadInvoice={(id) => handleDownloadInvoice(id)}
                    isDownloadBillEnable={true}
                    onDownloadBill={(id) => handleDownloadBill(id)}
                    //isViewEnable={true}
                    handleRowClick={(id) =>
                      navigateWithReturn(navigate, `/builder/inventory/orders/items/${id}`, {
                        returnTo: buildReturnToFromLocation(location)
                      })
                    }
                    isWrap={true}
                  />
                  <PaginationComponent
                    page={ordersPage}
                    totalPages={totalOrdersPage}
                    onPageChange={handleOrdersPageChange}
                    isFetching={isFetchingOrders}
                  />
                </>
              ) : (
                <NoDataFound disabledBtn={true} screenHeight={false} />
              ))}
          </div>
        </GridWrapper>
      </Layout>
    </>
  );
};

export default Orders;
